// draftMemory — universal correction-write wrapper. One entry point
// for every kitchen-inflow surface (AddItem, Shop Mode, Receipt
// pipeline once it lands). Routes to the right tier based on the
// `key` shape:
//
//   { upc: "0123456789012" }
//     → barcode_identity_corrections (admin) / user_scan_corrections
//       (family). Existing wiring; delegates to rememberBarcodeCorrection.
//
//   { canonicalAndBrand: { canonical, brand } }
//     → manual_corrections (admin) / user_manual_corrections (family).
//       Migration 0159. Lets the manual / wizard / photo pipelines
//       teach the cascade even when there's no UPC.
//
//   { canonicalOnly: "olive_oil" }
//     → manual_corrections with brand=null. Weak signal; treated as
//       a canonical-wide hint (e.g. "olive oil tends to live in the
//       condiments tile").
//
// All three key types accept the same `axes` payload so callers can
// share a single helper. Fire-and-forget per CLAUDE.md — a write
// failure must NEVER block the user's main flow.

import { supabase } from "./supabase";
import { rememberBarcodeCorrection } from "./barcodeCorrections";

/**
 * @param {object} args
 * @param {{upc?:string, canonicalAndBrand?:{canonical:string,brand:string},
 *          canonicalOnly?:string}} args.key
 * @param {{
 *   canonicalId?: string|null,
 *   typeId?: string|null,
 *   tileId?: string|null,
 *   location?: string|null,
 *   state?: string|null,
 *   cut?: string|null,
 *   brand?: string|null,
 *   name?: string|null,
 *   packageSizeAmount?: number|null,
 *   packageSizeUnit?: string|null,
 *   claims?: string[]|null,
 *   productMetadata?: object|null,
 *   ingredientIds?: string[]|null,
 *   categoryHints?: string[]|null,
 *   emoji?: string|null,
 * }} args.axes
 * @param {string} args.userId
 * @param {boolean} args.isAdmin
 */
export async function rememberCorrection({ key, axes, userId, isAdmin }) {
  if (!key || !axes || !userId) {
    return { error: new Error("rememberCorrection: key + axes + userId required") };
  }

  // UPC tier — delegate to the proven implementation.
  if (key.upc) {
    return rememberBarcodeCorrection({
      userId,
      isAdmin: !!isAdmin,
      barcodeUpc: key.upc,
      canonicalId: axes.canonicalId || null,
      typeId:      axes.typeId || null,
      tileId:      axes.tileId || null,
      location:    axes.location || null,
      state:       axes.state || null,
      cut:         axes.cut || null,
      brand:       axes.brand || null,
      variant:     axes.variant || null,
      name:        axes.name || null,
      packageSizeAmount: axes.packageSizeAmount ?? null,
      packageSizeUnit:   axes.packageSizeUnit ?? null,
      ingredients: axes.ingredientIds ?? null,
      categoryHints: axes.categoryHints ?? null,
      emoji: axes.emoji ?? null,
      claims: axes.claims ?? null,
      productMetadata: axes.productMetadata ?? null,
    });
  }

  // (canonical, brand) tier — manual_corrections (admin) or
  // user_manual_corrections (family). Migration 0159.
  const canonical =
    key.canonicalAndBrand?.canonical || key.canonicalOnly || null;
  const brand = key.canonicalAndBrand?.brand || null;
  // Variant — the flavor / product-line qualifier carried alongside
  // brand (migration 0160). Caller passes via axes.variant; key
  // stays brand-keyed but the write captures variant in the row.
  // NULL variant = bare brand (Pepsi without "Wild Cherry"); each
  // distinct variant gets its own row so the picker can surface
  // the specific product.
  const variant = axes.variant || null;
  if (!canonical) {
    return { error: new Error("rememberCorrection: canonical id required") };
  }

  const patch = cleanEmpty({
    type_id:        axes.typeId,
    tile_id:        axes.tileId,
    location:       axes.location,
    state:          axes.state,
    cut:            axes.cut,
    package_amount: axes.packageSizeAmount,
    package_unit:   axes.packageSizeUnit,
  });

  // Skip the round-trip ONLY for canonical-only writes with no
  // axes — the canonical's existence isn't news to anyone.
  //
  // (canonical, brand) pairs ARE a learning signal even when no
  // other axes changed: they tell the picker "this brand is
  // associated with this canonical" so the next user adding the
  // same canonical sees this brand surfaced. Without this, brand
  // observations would silently disappear (the cavatappi bug — user
  // associated Barilla with cavatappi, but every axis matched the
  // canonical's defaults so nothing got written, and the brand
  // never showed up in anyone else's picker). Allow brand-only
  // writes through; the row gets created with axis fields null and
  // correction_count=1; the bump RPC runs on each subsequent
  // observation.
  if (Object.keys(patch).length === 0 && (key.canonicalOnly || !brand)) {
    return { ok: true, skipped: "no-axes" };
  }

  // Upsert via explicit select → update-or-insert. We can't rely on
  // supabase-js .upsert({ onConflict }) here because migration 0160
  // replaced the plain unique constraint with an expression index
  // (coalesce(brand, ''), coalesce(variant, '')) which Postgres can't
  // infer for ON CONFLICT (...). Select-then-write is portable across
  // constraint shapes and handles NULL brand / NULL variant naturally
  // via "is" / equality match.
  if (isAdmin) {
    return upsertManualCorrection({
      table: "manual_corrections",
      match: { canonical_id: canonical, brand: brand || null, variant: variant || null },
      patch: { ...patch, last_seen_at: new Date().toISOString(), updated_at: new Date().toISOString() },
      onInsert: { correction_count: 1 },
      onUpdate: { correction_count_increment: true },
      tier: "global",
    });
  }

  return upsertManualCorrection({
    table: "user_manual_corrections",
    match: { user_id: userId, canonical_id: canonical, brand: brand || null, variant: variant || null },
    patch: { ...patch, last_seen_at: new Date().toISOString(), updated_at: new Date().toISOString() },
    onInsert: { correction_count: 1 },
    onUpdate: { correction_count_increment: true },
    tier: "family",
  });
}

// Select-then-update-or-insert. NULL-aware: maps each null match key
// to a .is(col, null) filter so the bare-brand row (variant=null) and
// each named variant row coexist correctly.
async function upsertManualCorrection({ table, match, patch, onInsert, onUpdate, tier }) {
  let q = supabase.from(table).select("id, correction_count");
  for (const [k, v] of Object.entries(match)) {
    q = v == null ? q.is(k, null) : q.eq(k, v);
  }
  const { data: existingRows, error: selErr } = await q.limit(1);
  if (selErr) return { error: selErr };
  const existing = existingRows && existingRows[0];
  if (existing) {
    const updates = { ...patch };
    if (onUpdate?.correction_count_increment) {
      updates.correction_count = (existing.correction_count || 1) + 1;
    }
    const { data, error } = await supabase
      .from(table)
      .update(updates)
      .eq("id", existing.id)
      .select()
      .single();
    if (error) return { error };
    return { ok: true, row: data, tier, existed: true };
  }
  const insertRow = { ...match, ...patch, ...(onInsert || {}) };
  const { data, error } = await supabase
    .from(table)
    .insert(insertRow)
    .select()
    .single();
  if (error) return { error };
  return { ok: true, row: data, tier, existed: false };
}

// Drop keys whose values are null/undefined/"". Avoids overwriting
// existing fields with empties on upsert.
function cleanEmpty(obj) {
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === null || v === undefined) continue;
    if (typeof v === "string" && v.trim() === "") continue;
    if (typeof v === "number" && !Number.isFinite(v)) continue;
    out[k] = v;
  }
  return out;
}
